import Observable from "zen-observable"

import {
  BasketInputItem,
  BasketItemType,
  BasketPaymentDetailsInput,
  BasketDiscountType,
  BasketTotalWithBreakDown,
  PaymentProvider,
  Address
} from "@irati/types"
import checkoutService from "../../services/checkout"
import optimisticBasket, {optimisticCashbackBasket} from "./helper/optimisticResponse"

export interface BasketSummary {
  items: BasketInputItem[]
  shippingAddress: Address
  breakdown: BasketTotalWithBreakDown
  isLoading: boolean
}

export type OnNext = (summary: BasketSummary) => void
export type OnError = (err: any) => void
export type OnComplete = () => void

class Basket {
  private observers: ZenObservable.Observer<BasketSummary>[] = []
  private _items?: BasketInputItem[]
  private _shippingAddress?: Address
  private _breakdown?: BasketTotalWithBreakDown
  private _paymentProvider = PaymentProvider.Adyen
  private _isCashback = false
  private isLoading = false

  get items() {
    return this._items || []
  }

  set items(items) {
    this._items = items
    this.fetchBreakdown()
  }

  get shippingAddress() {
    return this._shippingAddress || {}
  }

  set shippingAddress(shippingAddress) {
    this._shippingAddress = shippingAddress
  }

  get breakdown() {
    return this._breakdown || emptyBreakdown()
  }

  set breakdown(breakdown) {
    this._breakdown = breakdown
    this.isLoading = false
    this.updateObservers()
  }

  get paymentProvider() {
    return this._paymentProvider
  }

  set paymentProvider(paymentProvider) {
    this._paymentProvider = paymentProvider
    this.fetchBreakdown()
  }

  get isCashback() {
    return this._isCashback
  }

  set isCashback(val) {
    this._isCashback = val
    this.fetchBreakdown()
  }

  private updateObservers() {
    this.observers.forEach((observer) => {
      if (observer.next) {
        observer.next({items: this.items, breakdown: this.breakdown, isLoading: this.isLoading})
      }
    })
  }

  private async fetchBreakdown() {
    this.isLoading = true
    if (this.items.length) {
      this.updateObservers()

      // Take a snapshot so we can prevent race conditions
      const itemSnapshot = JSON.stringify(this.items)

      let optimisticRequest
      let paymentDetails: BasketPaymentDetailsInput = {discountType: BasketDiscountType.Standard}
      if (this._isCashback) {
        optimisticRequest = optimisticCashbackBasket
        paymentDetails = {discountType: BasketDiscountType.Cashback}
      } else {
        optimisticRequest = optimisticBasket
      }

      // Run queries with race condition prevention
      await optimisticRequest(this.items).then((optimistic) => {
        this.breakdown = shouldUpdateBreakdown(optimistic, this.breakdown, itemSnapshot, this.items)
      })
      await checkoutService.total(this.items, paymentDetails).then((response) => {
        this.breakdown = shouldUpdateBreakdown(response, this.breakdown, itemSnapshot, this.items)
      })
    } else {
      this.breakdown = emptyBreakdown()
    }
  }

  setProduct(
    entityType: BasketItemType,
    entityId: string,
    productId: string,
    {count, openValue}: Pick<BasketInputItem, "count" | "openValue">
  ) {
    const items = this.items
    let item = items.find((t) => t.entityId === entityId && t.productId === productId)
    if (!item) {
      item = {
        entityType,
        entityId,
        productId
      }
      items.push(item)
    }

    item.openValue = openValue
    item.count = count

    // trigger refresh
    if (!item.count && !item.openValue) {
      this.items = items.filter((t) => t !== item)
    } else {
      this.items = items
    }
  }

  async clear() {
    this.isCashback = false
    this.items = []
  }

  subscribe(onNext: OnNext, onError?: OnError, onComplete?: OnComplete) {
    return new Observable<BasketSummary>((observer) => {
      observer.next({items: this.items, breakdown: this.breakdown, isLoading: this.isLoading, shippingAddress: this.shippingAddress})
      this.observers.push(observer)
      return () => {
        this.observers = this.observers.filter((t) => t !== observer)
      }
    }).subscribe(onNext, onError, onComplete)
  }
}

const basket = new Basket()

export default basket

function emptyBreakdown(): BasketTotalWithBreakDown {
  return {items: [], total: {discountPercentage: 0, originalPrice: 0, amount: 0, currency: "", vat: 0}}
}

/**
 * @description: Prevents a race condition on requests coming back by snapshotting the request
 * params at execute time and comparing at response time.
 */
function shouldUpdateBreakdown(
  newData: BasketTotalWithBreakDown,
  oldData: BasketTotalWithBreakDown,
  snapshot: string,
  current: BasketInputItem[]
) {
  return snapshot === JSON.stringify(current) ? newData : oldData
}
